/**
 * Read-only sub-views for the public Tournaments page: knockout bracket tree,
 * league standings, league fixtures and scheduled-match list. All are pure
 * presentation components driven by the data the parent page fetches.
 */
import { Clock, TreeStructure, Calendar as CalendarIcon, Table as TableIcon } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// ============== Knockout bracket ==============
function BracketMatch({ match }) {
  const p1Won = match.winner_id === match.player1_id;
  const p2Won = match.winner_id === match.player2_id;
  return (
    <div
      className="bracket-match bg-card border border-border rounded-lg overflow-hidden min-w-[200px]"
      data-testid={`bracket-match-${match.match_id}`}
    >
      <div className={`flex items-center justify-between px-3 py-2 text-sm ${p1Won ? "bg-primary/15 text-primary font-medium" : "text-foreground"}`}>
        <span className="truncate max-w-[140px]">{match.player1_name || "BYE"}</span>
        <span className="font-heading font-bold ml-2">{match.player1_score ?? 0}</span>
      </div>
      <div className="h-px bg-border" />
      <div className={`flex items-center justify-between px-3 py-2 text-sm ${p2Won ? "bg-primary/15 text-primary font-medium" : "text-foreground"}`}>
        <span className="truncate max-w-[140px]">{match.player2_name || "BYE"}</span>
        <span className="font-heading font-bold ml-2">{match.player2_score ?? 0}</span>
      </div>
    </div>
  );
}

export function BracketView({ bracket }) {
  if (!bracket || !bracket.rounds) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <TreeStructure className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No bracket generated for this tournament</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto pb-4" data-testid="bracket-view">
      <div className="flex gap-8 min-w-max p-4">
        {bracket.rounds.map((round, roundIdx) => (
          <div key={round.round_name || `round-${roundIdx}`} className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-center text-muted-foreground uppercase tracking-wider">
              {round.round_name}
            </h4>
            <div className="flex flex-col gap-6 justify-around flex-1">
              {round.matches.map((match, matchIdx) => (
                <BracketMatch key={match.match_id || matchIdx} match={match} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== League standings ==============
export function LeagueStandingsView({ standings }) {
  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <TableIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No league standings available</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="league-standings">
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="w-10">#</TableHead>
            <TableHead>Player</TableHead>
            <TableHead className="text-center">P</TableHead>
            <TableHead className="text-center">W</TableHead>
            <TableHead className="text-center">L</TableHead>
            <TableHead className="text-center">F+</TableHead>
            <TableHead className="text-center">F-</TableHead>
            <TableHead className="text-center">FD</TableHead>
            <TableHead className="text-center font-bold">Pts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((s, i) => (
            <TableRow key={s.player_id} className="border-border">
              <TableCell className="font-bold">{i + 1}</TableCell>
              <TableCell className="font-medium">{s.player_name}</TableCell>
              <TableCell className="text-center">{s.played}</TableCell>
              <TableCell className="text-center text-primary">{s.won}</TableCell>
              <TableCell className="text-center text-destructive">{s.lost}</TableCell>
              <TableCell className="text-center">{s.frames_won}</TableCell>
              <TableCell className="text-center">{s.frames_lost}</TableCell>
              <TableCell className="text-center">{s.frame_diff >= 0 ? `+${s.frame_diff}` : s.frame_diff}</TableCell>
              <TableCell className="text-center font-heading font-bold text-primary">{s.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============== League fixtures ==============
export function LeagueFixturesView({ fixtures }) {
  if (!fixtures || fixtures.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No fixtures generated</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="league-fixtures">
      {fixtures.map((round) => (
        <div key={round.round}>
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Round {round.round}
          </h4>
          <div className="space-y-2">
            {round.fixtures.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                <span className="flex-1 text-right font-medium text-sm">{f.player1_name}</span>
                {f.is_played ? (
                  <div className="px-3 py-1 rounded bg-background">
                    <span className="font-heading font-bold text-sm">{f.player1_score ?? 0}</span>
                    <span className="mx-1 text-muted-foreground">-</span>
                    <span className="font-heading font-bold text-sm">{f.player2_score ?? 0}</span>
                  </div>
                ) : (
                  <span className="px-3 py-1 text-xs text-muted-foreground">vs</span>
                )}
                <span className="flex-1 font-medium text-sm">{f.player2_name}</span>
                {f.scheduled_date && (
                  <span className="text-xs text-muted-foreground">{f.scheduled_date}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============== Scheduled matches list ==============
export function ScheduledMatchesView({ matches }) {
  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No scheduled matches</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="scheduled-matches">
      {matches.map((match) => (
        <div key={match.id} className="flex items-center gap-4 p-4 rounded-lg border border-border bg-muted/20">
          <div className="flex-1 text-right">
            <p className="font-medium text-sm">{match.player1_name}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground font-medium">{match.tournament_name}</p>
            <p className="text-xs text-primary mt-0.5">
              {match.scheduled_date} {match.scheduled_time && `at ${match.scheduled_time}`}
            </p>
            {match.table_name && (
              <p
                className="text-[10px] text-secondary font-medium mt-0.5"
                data-testid={`upcoming-table-${match.id}`}
              >
                {match.table_name}
              </p>
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">{match.player2_name}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
